import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, Gauge, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AssistantComposer } from "./AssistantComposer";
import { AssistantMessage } from "./AssistantMessage";
import { AssistantMobileMenuButton, AssistantSidebar } from "./AssistantSidebar";
import { modeDetails } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "marez_private_assistant_session";
const newId = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const greeting = (mode) => ({ id: newId(), role: "assistant", content: modeDetails(mode).greeting, greeting: true });

const loadSession = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    if (saved?.sessionId && Array.isArray(saved.messages)) return saved;
  } catch { /* ignore invalid browser session */ }
  return { sessionId: newId(), mode: "general", model: "gemini-3-flash-preview", messages: [greeting("general")] };
};

export const AssistantWorkspace = ({ user, onLogout, onUnauthorized }) => {
  const initial = useMemo(loadSession, []);
  const [sessionId, setSessionId] = useState(initial.sessionId);
  const [mode, setMode] = useState(initial.mode || "general");
  const [model, setModel] = useState(initial.model || "gemini-3-flash-preview");
  const [messages, setMessages] = useState(initial.messages);
  const [streaming, setStreaming] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, mode, model, messages }));
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [sessionId, mode, model, messages]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const resetChat = () => {
    abortRef.current?.abort();
    setStreaming(false); setSessionId(newId()); setMessages([greeting(mode)]); setRemaining(null);
    toast.success("New private chat started");
  };

  const changeMode = (nextMode) => {
    setMode(nextMode);
    if (!messages.some((message) => message.role === "user")) setMessages([greeting(nextMode)]);
  };

  const streamMessage = async (text, baseMessages = messages, showUser = true) => {
    if (!text.trim() || streaming) return;
    const historyBase = showUser ? baseMessages : baseMessages.slice(0, -1);
    const userMessage = { id: newId(), role: "user", content: text.trim() };
    const assistantMessage = { id: newId(), role: "assistant", content: "", streaming: true };
    const visibleMessages = showUser ? [...baseMessages, userMessage, assistantMessage] : [...baseMessages, assistantMessage];
    setMessages(visibleMessages); setStreaming(true);
    const controller = new AbortController(); abortRef.current = controller;

    try {
      const response = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text.trim(), mode, model, history: historyBase.filter((item) => !item.greeting && item.content).slice(-10).map(({ role, content }) => ({ role, content })) }),
      });
      if (response.status === 401 || response.status === 403) { onUnauthorized(); throw new Error("Your private session expired. Please sign in again."); }
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || "The assistant could not respond.");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let responseText = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() || "";
        for (const block of blocks) {
          const line = block.split("\n").find((item) => item.startsWith("data: "));
          if (!line) continue;
          const event = JSON.parse(line.slice(6));
          if (event.type === "meta") setRemaining(event.remaining);
          if (event.type === "delta") {
            responseText += event.content;
            setMessages((current) => current.map((item) => item.id === assistantMessage.id ? { ...item, content: responseText } : item));
          }
          if (event.type === "error") throw new Error(event.message);
        }
      }
      setMessages((current) => current.map((item) => item.id === assistantMessage.id ? { ...item, content: responseText || "No response was generated.", streaming: false } : item));
    } catch (error) {
      if (error.name === "AbortError") {
        setMessages((current) => current.map((item) => item.id === assistantMessage.id ? { ...item, content: item.content || "Generation stopped.", streaming: false } : item));
      } else {
        setMessages((current) => current.map((item) => item.id === assistantMessage.id ? { ...item, content: error.message || "The assistant is unavailable.", error: true, streaming: false } : item));
      }
    } finally {
      setStreaming(false); abortRef.current = null;
    }
  };

  const retry = (assistantId) => {
    const assistantIndex = messages.findIndex((item) => item.id === assistantId);
    if (assistantIndex < 1) return;
    const base = messages.slice(0, assistantIndex);
    const priorUser = [...base].reverse().find((item) => item.role === "user");
    if (!priorUser) return;
    setMessages(base);
    window.setTimeout(() => streamMessage(priorUser.content, base, false), 0);
  };

  const currentMode = modeDetails(mode);
  return <main data-testid="assistant-workspace" className="h-screen overflow-hidden bg-[#050505] text-white grain flex">
    <AssistantSidebar mode={mode} model={model} user={user} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} onModeChange={changeMode} onModelChange={setModel} onNewChat={resetChat} onLogout={onLogout} />
    <section className="min-w-0 flex-1 h-screen flex flex-col">
      <header data-testid="assistant-workspace-header" className="h-16 shrink-0 border-b border-white/10 bg-[#080808] px-4 sm:px-6 flex items-center justify-between"><div className="flex items-center gap-3"><AssistantMobileMenuButton onClick={() => setMobileOpen(true)} /><div><p data-testid="assistant-current-mode" className="font-heading text-sm sm:text-base font-semibold">{currentMode.label}</p><p data-testid="assistant-current-model" className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">{model === "gemini-3-flash-preview" ? "Gemini 3 Flash · Low credit" : "GPT-5.4 mini · Deep help"}</p></div></div>{remaining && <div data-testid="assistant-usage-indicator" className="hidden sm:flex items-center gap-2 font-mono text-[9px] uppercase text-zinc-600"><Gauge size={12} /> {remaining.day} daily prompts left</div>}</header>
      <div ref={scrollRef} data-testid="assistant-conversation-feed" className="flex-1 overflow-y-auto"><div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">{messages.map((message) => <AssistantMessage key={message.id} message={message} onRetry={retry} />)}</div></div>
      <AssistantComposer streaming={streaming} onSend={streamMessage} onStop={() => abortRef.current?.abort()} />
    </section>
    <aside data-testid="assistant-presets-panel" className="hidden xl:flex w-72 shrink-0 h-screen border-l border-white/10 bg-[#080808] flex-col p-5"><div className="aspect-[16/9] rounded-lg overflow-hidden relative"><img src="/porsche-gt3rs-finished.webp" alt="Marez Detailing Porsche finish" className="h-full w-full object-cover object-[center_68%]" /><div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" /><p className="absolute left-3 bottom-3 font-mono text-[9px] uppercase tracking-widest text-white">Marez intelligence</p></div><div className="mt-7"><p className="font-mono text-[9px] uppercase tracking-widest text-zinc-700">Start with a prompt</p><div className="mt-3 space-y-2">{currentMode.presets.map((preset, index) => <button data-testid={`assistant-preset-${mode}-${index}`} key={preset} type="button" disabled={streaming} onClick={() => streamMessage(preset)} className="w-full rounded-lg border border-white/8 bg-[#0d0d0d] p-3 text-left text-xs text-zinc-400 hover:text-white hover:border-white/20 transition-colors flex items-start justify-between gap-2"><span>{preset}</span><ChevronRight size={12} className="shrink-0 mt-0.5" /></button>)}</div></div><div className="mt-auto border-t border-white/10 pt-5"><p className="flex items-center gap-2 text-xs text-zinc-400"><Sparkles size={13} /> Credit-conscious by default</p><p className="text-[10px] leading-relaxed text-zinc-700 mt-2">Responses stay concise. Choose GPT-5.4 mini only when a harder question needs it.</p></div></aside>
  </main>;
};
